import { CurrencyLabelMenuItem } from '@components/CurrencyLabelMenuItem';
import type { WorkflowCompletion } from '@generated/graphql';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SegmentedControl } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { BankAccountSelectField } from '@ovComponents/3-pattern/bankAccountSelectField/bankAccountSelect';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { MULTI_PORTFOLIO_DEPOSIT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Typography } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { AllocationMode } from '../utils/allocationSplit';
import { toAmountCents } from '../utils/depositAmountRules';
import { DepositScheduleFields } from './DepositScheduleFields';
import { MultiPortfolioDepositHeader } from './MultiPortfolioDepositHeader';
import type { DepositEligibleAccount } from './useDepositEligibleSubAccounts';
import { useMultiPortfolioDeposit } from './useMultiPortfolioDeposit';

export type MultiPortfolioDepositProps = {
  options: any;
  onNext: () => void | Promise<void>;
  stepLoading?: boolean;
  workflowCompletion?: WorkflowCompletion;
  dummyDeposit?: any;
  setDummyDeposit?: any;
};

const accountOptionLabel = (
  t: TFunction,
  { account }: DepositEligibleAccount,
) => {
  const accountType = t(`accountTypes:${account.type}`);

  const name = account.custodianAccountNumber
    ? `${accountType} #${account.custodianAccountNumber}`
    : accountType;

  const marketValueCents = account.statistics?.marketValueCents;

  return marketValueCents
    ? `${name} (${formatMoneyValue(marketValueCents)})`
    : name;
};

/**
 * Splits one deposit across the eligible sub-accounts of a primary account.
 * Each allocated sub-account becomes its own temporary deposit for Deposit Review.
 */
export const MultiPortfolioDeposit = ({
  options,
  onNext,
  workflowCompletion,
  dummyDeposit,
  setDummyDeposit,
}: MultiPortfolioDepositProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const [mode, setMode] = useState<AllocationMode>('amount');

  const {
    userId,
    accounts,
    loading,
    accountId,
    selectedAccount,
    subAccounts,
    depositData,
    updateContextWithFields,
    isEft,
    schedule,
    focused,
    focus,
    showsFieldError,
    selectAccount,
    submit,
  } = useMultiPortfolioDeposit({
    options,
    onNext,
    workflowCompletion,
    dummyDeposit,
    setDummyDeposit,
  });

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        dataTestId: MULTI_PORTFOLIO_DEPOSIT_TEST_ID.CONTINUE_BUTTON,
      }}
    >
      <MultiPortfolioDepositHeader options={options} />
      <SelectField
        label={translateBackendString(options?.primaryAccount?.label)}
        value={accountId}
        onChange={(event: { target: { value: string } }) =>
          selectAccount(event.target.value)
        }
        testId={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.ACCOUNT_SELECT}
        disabled={loading}
        fullWidth
      >
        {accounts.map((eligibleAccount) => {
          const { account } = eligibleAccount;
          return (
            <MenuItem
              key={account.id}
              value={account.id}
              data-testid={`${MULTI_PORTFOLIO_DEPOSIT_TEST_ID.ACCOUNT_SELECT}-${account.id}`}
            >
              <CurrencyLabelMenuItem
                label={accountOptionLabel(t, eligibleAccount)}
                currency={account.baseCurrency}
                multiCurrency={(account.availableCurrencies?.length ?? 0) > 1}
                showAvatar
              />
            </MenuItem>
          );
        })}
      </SelectField>
      {isEft && options?.bankAccount?.enabled && (
        <BankAccountSelectField
          key={`bank-account-select-${accountId}`}
          userId={userId ?? ''}
          sx={{ mt: 2 }}
          disabled={!userId}
          setBankAccount={(bankAccount) =>
            updateContextWithFields({ bankAccount: bankAccount ?? undefined })
          }
          bankAccount={depositData?.bankAccount || null}
          accountCurrencies={selectedAccount?.availableCurrencies}
          onBlur={() => focus('bankAccount')}
          error={showsFieldError('bankAccount')}
          options={options}
          showSelectedIcon
        />
      )}
      {options?.amountCents?.enabled && (
        <AmountField
          useFormatAmountValueCents
          sx={{ mt: 2, mb: 2 }}
          testId={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.TOTAL_AMOUNT}
          label={translateBackendString(options?.amountCents?.label)}
          setAmount={(amount: string) =>
            updateContextWithFields({ amountCents: toAmountCents(amount) })
          }
          amount={depositData?.amountCents}
          onBlur={() => focus('amountCents')}
          error={showsFieldError('amountCents')}
        />
      )}
      {options?.usePercentAllocation && (
        <Box sx={{ width: 160, mb: 2 }}>
          <SegmentedControl
            enforceActive
            testId={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.MODE_TOGGLE}
            value={mode}
            segments={[
              { value: 'amount', label: '$' },
              { value: 'percent', label: '%' },
            ]}
            onChange={(value) => setMode(value as AllocationMode)}
          />
        </Box>
      )}
      {subAccounts.map(({ id, financialProduct, statistics }) => (
        <Typography
          key={id}
          variant='bodyMedium'
          data-testid={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.SUB_ACCOUNT_ROW}
        >
          {`${translateBackendString(financialProduct?.translatedName)} (${formatMoneyValue(statistics?.marketValueCents ?? undefined)} ${statistics?.currency})`}
        </Typography>
      ))}
      <DepositScheduleFields
        options={options}
        depositData={depositData}
        updateContextWithFields={updateContextWithFields}
        schedule={schedule}
        focused={focused}
        focus={focus}
        showsFieldError={showsFieldError}
      />
    </WorkflowCompletionForm>
  );
};

export default MultiPortfolioDeposit;
